import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Stack,
  Box,
  Typography,
  Chip,
  Tooltip,
  InputAdornment,
  Popover,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  CircularProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  KeyboardArrowDown as ArrowDownIcon,
} from "@mui/icons-material";
import LayerdIcon from "../../../common/LayerdIcon";
import { DETECTION_ICONS } from "../../../../icons/detectionLucideIcons";
import { Filter } from "lucide-react";
import colors from "../../../../utils/colors";
import { getResStatusString } from "../../../pages/application/analytics/common";
import RefreshIconButton from "../../../common/RefreshIconButton";
import { getWafEventFilterValue, getWafEventStatus } from "./wafEventListUtils";
import { getBotEventFilterValue } from "./botEventDrawerData";
import { SafeTextField } from "../../../common/Greens";
import { filterInputSx } from "../services/api_security/asecUi";
import { eventSearchInputSx } from "./eventSearchInputSx";

const brandBlue = colors.primary[300];

const RATE_LIMIT_STATUS_OPTIONS = ["Blocked", "Challenge", "Log only"];
const HTTP_METHOD_FILTER_OPTIONS = ["GET", "POST", "HEAD", "PUT", "DELETE", "PATCH", "OPTIONS", "TRACE", "CONNECT"];

function methodFilterField(description) {
  return {
    id: "method",
    label: "Method",
    description,
    options: HTTP_METHOD_FILTER_OPTIONS,
  };
}

function isStaticOptionsFilter(filter) {
  return Array.isArray(filter?.options) && filter.options.length > 0;
}

const QUICK_FILTER_DEFS = {
  src_ip: {
    id: "src_ip",
    label: "IP Address",
    icon: DETECTION_ICONS.ip,
    placeholder: "Filter by IP",
  },
  hostname: {
    id: "hostname",
    label: "Hostname",
    icon: DETECTION_ICONS.hostname,
    placeholder: "Filter by hostname",
  },
  uri: {
    id: "uri",
    label: "URI Path",
    icon: DETECTION_ICONS.uri,
    placeholder: "Filter by URI",
  },
  status: {
    id: "status",
    label: "Status",
    icon: DETECTION_ICONS.status,
    placeholder: "Filter by status",
  },
};

function getRateLimitStatusFilterLabel(event) {
  const action = String(event?.action || "").trim().toLowerCase();
  const blocked = event?.blocked_bool || event?.sd_rate_limited === 1;
  if (action === "block" || blocked) return "Blocked";
  if (action === "challenge") return "Challenge";
  if (action === "log_only") return "Log only";
  return null;
}

function getEventFilterValue(event, filter, eventType) {
  if (!event || !filter) return undefined;
  if (eventType === "waf") {
    return getWafEventFilterValue(event, filter.id);
  }
  if (eventType === "bot") {
    return getBotEventFilterValue(event, filter.id);
  }
  let val = event[filter.id];
  if (filter.id === "hostname") val = event.host_name || event.hostname;
  if (filter.id === "status" && eventType === "firewall") {
    val = getWafEventStatus(event);
  } else if (filter.id === "status" && eventType === "ratelimit") {
    val = getRateLimitStatusFilterLabel(event);
  } else if (filter.id === "status") {
    const code = event.res_status ?? event.resStatus;
    if (code !== undefined && code !== null) {
      val = getResStatusString(code);
    } else if (event.action) {
      const a = String(event.action).toLowerCase();
      if (a === "block" || a === "blocked") val = "Blocked";
      else if (a === "challenge" || a === "challenged") val = "Challenged";
      else if (a === "detect" || a === "passed" || a === "logged") val = "Passed";
    }
  }
  return val;
}

function collectUniqueFilterValues(events, filter, eventType, limit = 20) {
  if (!filter || !events) return [];
  if (filter.options?.length) return filter.options;
  const values = new Set();
  events.forEach((event) => {
    const val = getEventFilterValue(event, filter, eventType);
    if (Array.isArray(val)) {
      val.forEach((v) => values.add(String(v)));
    } else if (val !== undefined && val !== null && val !== "") {
      values.add(String(val));
    }
  });
  return Array.from(values).slice(0, limit);
}

function getPredefinedFilters(eventType) {
  const { src_ip, hostname, uri, status } = QUICK_FILTER_DEFS;
  return [src_ip, hostname, uri, status];
}

const PredefinedFilterDropdown = ({ filter, events, value, onChange, onClear, eventType }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const open = Boolean(anchorEl);
  const IconComponent = filter.icon;
  const staticOptions = isStaticOptionsFilter(filter);

  useEffect(() => {
    if (open) {
      setInputValue(value || "");
    }
  }, [open, value]);

  const uniqueValues = useMemo(
    () => collectUniqueFilterValues(events, filter, eventType, 15),
    [events, filter, eventType],
  );

  const filteredValues = useMemo(() => {
    if (staticOptions || !inputValue) return uniqueValues;
    return uniqueValues.filter((v) =>
      String(v).toLowerCase().includes(inputValue.toLowerCase())
    );
  }, [inputValue, uniqueValues, staticOptions]);

  const handleApply = (selectedValue) => {
    onChange(filter.id, selectedValue);
    setAnchorEl(null);
    setInputValue("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && inputValue.trim()) {
      handleApply(inputValue.trim());
    }
  };

  return (
    <>
      <Box
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          height: 36,
          borderRadius: "8px",
          border: `1px solid ${value ? brandBlue : colors.neutral[200]}`,
          backgroundColor: value ? "rgba(99, 113, 247, 0.06)" : "#fff",
          cursor: "pointer",
          minWidth: 140,
          transition: "border-color 140ms ease, background-color 140ms ease, box-shadow 140ms ease, transform 120ms ease",
          "&:hover": {
            borderColor: brandBlue,
            backgroundColor: "rgba(99, 113, 247, 0.08)",
            boxShadow: "0 1px 4px rgba(99, 113, 247, 0.12)",
            "& .filter-label": {
              color: brandBlue,
            },
          },
          "&:active": {
            transform: "scale(0.98)",
          },
        }}
      >
        <LayerdIcon icon={IconComponent} size={16} color="primary" />
        <Typography
          className="filter-label"
          sx={{
            fontSize: 14,
            color: value ? brandBlue : colors.neutral[800],
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {value || filter.label}
        </Typography>
        {value ? (
          <CloseIcon
            onClick={(e) => {
              e.stopPropagation();
              onClear(filter.id);
            }}
            sx={{ fontSize: 14, color: brandBlue, "&:hover": { color: colors.red[100] } }}
          />
        ) : (
          <ArrowDownIcon sx={{ fontSize: 16, color: brandBlue }} />
        )}
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setInputValue("");
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: "8px",
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.12)",
            border: `1px solid ${colors.neutral[200]}`,
            width: 240,
            maxHeight: 320,
            backgroundColor: "#fff",
          },
        }}
      >
        <Box sx={{ p: staticOptions ? 0 : 1.5 }}>
          {!staticOptions && (
            <SafeTextField
              fullWidth
              placeholder={filter.placeholder}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              size="small"
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: colors.neutral[400] }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                ...filterInputSx,
              }}
            />
          )}
        </Box>
        {!staticOptions && <Divider />}
        <Box sx={{ maxHeight: 200, overflow: "auto", backgroundColor: "#fff", pt: staticOptions ? 0.5 : 0 }}>
          <List disablePadding dense>
            {filteredValues.length > 0 ? (
              filteredValues.map((val, idx) => (
                <ListItem key={idx} disablePadding>
                  <ListItemButton
                    onClick={() => handleApply(val)}
                    sx={{
                      py: 0.75,
                      px: 1.5,
                      "&:hover": { backgroundColor: "#f0f4ff" },
                    }}
                  >
                    <ListItemText
                      primary={val}
                      primaryTypographyProps={{
                        sx:  { fontSize: 14, color: colors.neutral[800] },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))
            ) : (
              <Box sx={{ p: 2, textAlign: "center", backgroundColor: "#fff" }}>
                <Typography sx={ { fontSize: 14, color: colors.neutral[500] }}>
                  {inputValue ? "No matches found" : "No values available"}
                </Typography>
              </Box>
            )}
          </List>
        </Box>
        {inputValue && !staticOptions && !filteredValues.includes(inputValue) && (
          <>
            <Divider />
            <Box sx={{ p: 1 }}>
              <ListItemButton
                onClick={() => handleApply(inputValue)}
                sx={{
                  py: 0.75,
                  px: 1.5,
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                  border: `1px solid ${colors.primary[300]}`,
                  "&:hover": { backgroundColor: "#f0f4ff" },
                }}
              >
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <AddIcon sx={{ fontSize: 16, color: colors.primary[300] }} />
                </ListItemIcon>
                <ListItemText
                  primary={`Use "${inputValue}"`}
                  primaryTypographyProps={{
                    sx:  { fontSize: 14, color: colors.primary[300], fontWeight: 500 },
                  }}
                />
              </ListItemButton>
            </Box>
          </>
        )}
      </Popover>
    </>
  );
};

PredefinedFilterDropdown.propTypes = {
  filter: PropTypes.object.isRequired,
  events: PropTypes.array,
  eventType: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
};

const filterCategories = {
  waf: [
    {
      id: "event",
      label: "Event",
      filters: [
        { id: "id", label: "Event ID", description: "The unique identifier for the event" },
        { id: "waf_type", label: "Source", description: "Detection engine (Layerd Signature WAF / Layerd AI Smart Parse)", options: ["signature_waf", "smart_parse"] },
        { id: "hostname", label: "Hostname", description: "Target hostname (e.g. www.example.com)" },
        { id: "src_ip", label: "IP Address", description: "Source IP address" },
        { id: "country", label: "Country", description: "Country ISO code (e.g. GB, US)" },
        { id: "status", label: "Status", description: "WAF action taken (Blocked, Challenged, Passed)", options: ["Blocked", "Challenged", "Passed"] },
      ],
    },
    {
      id: "request",
      label: "Request",
      filters: [
        methodFilterField("HTTP method (GET, POST, PUT, etc.)"),
        { id: "uri", label: "URI Path", description: "Request URI/path (e.g. /api/login)" },
        { id: "res_code", label: "Response Status", description: "HTTP response status code (e.g. 403, 200)" },
        // { id: "user_agent", label: "User Agent", description: "User agent string" },
        { id: "ua", label: "User Agent (alias)", description: "Alias for user_agent" },
        { id: "ja3_hash", label: "JA3 Fingerprint", description: "TLS fingerprint hash" },
      ],
    },
    {
      id: "detection",
      label: "Detection",
      filters: [
        { id: "attack_type", label: "Attack Type", description: "Attack type (e.g. Cross-Site Scripting (XSS))" },
        { id: "verdict", label: "Verdict", description: "Smart Parse verdict (malicious, suspicious)", options: ["malicious", "suspicious"] },
        // { id: "type", label: "Match Message", description: "Signature WAF rule match message" },
        // { id: "rule_id", label: "Rule ID", description: "The specific rule ID that was triggered" },
      ],
    },
  ],
  firewall: [
    {
      id: "event",
      label: "Event",
      filters: [
        { id: "id", label: "Event ID", description: "The unique identifier for the event" },
        { id: "hostname", label: "Hostname", description: "The hostname or domain name of the request target" },
        { id: "src_ip", label: "IP Address", description: "The source IP address that initiated the request" },
        { id: "country", label: "Country", description: "The country of origin based on IP geolocation" },
        { id: "status", label: "Status", description: "The action taken on the request (Blocked, Challenged, Passed)", options: ["Blocked", "Challenged", "Passed"] },
      ],
    },
    {
      id: "request",
      label: "Request",
      filters: [
        methodFilterField("The HTTP method used in the request (GET, POST, PUT, etc.)"),
        { id: "uri", label: "URI Path", description: "The URI path of the request" },
        { id: "resStatus", label: "Response Status", description: "The HTTP response status code" },
      ],
    },
    {
      id: "detection",
      label: "Detection",
      filters: [
        { id: "operator", label: "Match Type", description: "The type of firewall match that was triggered" },
        { id: "block_reason", label: "Block Reason", description: "The reason the request was blocked" },
      ],
    },
  ],
  bot: [
    {
      id: "event",
      label: "Event",
      filters: [
        { id: "id", label: "Event ID", description: "The unique identifier for the event" },
        { id: "hostname", label: "Hostname", description: "The hostname or domain name of the request target" },
        { id: "src_ip", label: "IP Address", description: "The source IP address that initiated the request" },
        { id: "country", label: "Country", description: "The country of origin based on IP geolocation" },
        { id: "status", label: "Status", description: "The action taken on the request (Blocked, Challenged, Detect)", options: ["Blocked", "Challenged", "Detect"] },
      ],
    },
    {
      id: "request",
      label: "Request",
      filters: [
        methodFilterField("The HTTP method used in the request (GET, POST, PUT, etc.)"),
        { id: "uri", label: "URI Path", description: "The URI path of the request" },
        { id: "resStatus", label: "Response Status", description: "The HTTP response status code" },
      ],
    },
    {
      id: "detection",
      label: "Detection",
      filters: [
        { id: "bot_type", label: "Bot Type", description: "The category of bot detected (Good Bot, Bad Bot, etc.)" },
        { id: "bot_name", label: "Bot Name", description: "The specific name or identifier of the bot" },
      ],
    },
  ],
  ratelimit: [
    {
      id: "event",
      label: "Event",
      filters: [
        { id: "id", label: "Event ID", description: "The unique identifier for the event" },
        { id: "hostname", label: "Hostname", description: "The hostname or domain name of the request target" },
        { id: "src_ip", label: "IP Address", description: "The source IP address that initiated the request" },
        { id: "country", label: "Country", description: "The country of origin based on IP geolocation" },
        { id: "status", label: "Status", description: "The action taken on the request (Blocked, Challenge, Log only)", options: RATE_LIMIT_STATUS_OPTIONS },
      ],
    },
    {
      id: "request",
      label: "Request",
      filters: [
        methodFilterField("The HTTP method used in the request (GET, POST, PUT, etc.)"),
        { id: "uri", label: "URI Path", description: "The URI path of the request" },
        { id: "resStatus", label: "Response Status", description: "The HTTP response status code" },
      ],
    },
    {
      id: "detection",
      label: "Detection",
      filters: [
        { id: "ja3_hash", label: "JA3 Fingerprint", description: "The TLS fingerprint of the client connection" },
      ],
    },
  ],
  smartparse: [
    {
      id: "event",
      label: "Event",
      filters: [
        { id: "id", label: "Event ID", description: "The unique identifier for the event" },
        { id: "hostname", label: "Hostname", description: "The hostname or domain name of the request target" },
        { id: "src_ip", label: "IP Address", description: "The source IP address that initiated the request" },
        { id: "country", label: "Country", description: "The country of origin based on IP geolocation" },
        { id: "status", label: "Status", description: "The action taken on the request (Blocked, Challenged, Passed)", options: ["Blocked", "Challenged", "Passed"] },
      ],
    },
    {
      id: "request",
      label: "Request",
      filters: [
        methodFilterField("The HTTP method used in the request (GET, POST, PUT, etc.)"),
        { id: "uri", label: "URI Path", description: "The URI path of the request" },
      ],
    },
    {
      id: "detection",
      label: "Detection",
      filters: [
        { id: "attack_type", label: "Attack Type", description: "The type of attack detected (XSS, SQL Injection, etc.)" },
        { id: "risk_level", label: "Risk Level", description: "The severity level of the detected threat" },
        { id: "verdict", label: "Verdict", description: "The verdict of the detection (malicious, suspicious, etc.)" },
      ],
    },
  ],
};

const USER_AGENT_FILTER_IDS = new Set(["ua", "user_agent"]);
const USER_AGENT_CHIP_MAX_CHARS = 20;

function formatFilterChipValue(filterId, value) {
  const raw = String(value ?? "");
  if (!USER_AGENT_FILTER_IDS.has(filterId) || raw.length <= USER_AGENT_CHIP_MAX_CHARS) {
    return { display: raw, tooltip: null };
  }
  return {
    display: `${raw.slice(0, USER_AGENT_CHIP_MAX_CHARS)}...`,
    tooltip: raw,
  };
}

const FilterChip = ({ filter, value, onRemove, onClick }) => {
  const { display, tooltip } = formatFilterChipValue(filter?.id, value);
  const valueTypography = (
    <Typography sx={{ fontSize: 14, color: colors.primary[300] }}>
      {display}
    </Typography>
  );

  return (
    <Chip
      label={
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
            {filter.label}
          </Typography>
          {value && (
            <>
              <Typography sx={{ fontSize: 14, color: colors.neutral[500] }}>:</Typography>
              {tooltip ? (
                <Tooltip title={tooltip} arrow>
                  <Box component="span" sx={{ display: "inline-flex", maxWidth: "100%" }}>
                    {valueTypography}
                  </Box>
                </Tooltip>
              ) : (
                valueTypography
              )}
            </>
          )}
          <ArrowDownIcon sx={{ fontSize: 16, color: colors.neutral[600], ml: 0.5 }} />
        </Stack>
      }
      onClick={onClick}
      onDelete={onRemove}
      deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
      sx={{
        height: 36,
        borderRadius: "8px",
        backgroundColor: "#fff",
        border: `1px solid ${colors.neutral[200]}`,
        "& .MuiChip-label": { px: 1.5 },
        "& .MuiChip-deleteIcon": {
          color: colors.neutral[500],
          "&:hover": { color: colors.neutral[700] },
        },
        "&:hover": {
          borderColor: colors.primary[300],
        },
      }}
    />
  );
};

FilterChip.propTypes = {
  filter: PropTypes.object.isRequired,
  value: PropTypes.string,
  onRemove: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
};

const FilterDropdown = ({ anchorEl, open, onClose, eventType, onSelectFilter, activeFilters, barFilterIds = [] }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [hoveredFilter, setHoveredFilter] = useState(null);

  const categories = useMemo(() => filterCategories[eventType] || [], [eventType]);

  const filteredFilters = useMemo(() => {
    if (!searchTerm) return null;
    const term = searchTerm.toLowerCase();
    const results = [];
    categories.forEach((category) => {
      category.filters.forEach((filter) => {
        if (
          filter.label.toLowerCase().includes(term) ||
          filter.description.toLowerCase().includes(term)
        ) {
          results.push({ ...filter, category: category.label });
        }
      });
    });
    return results;
  }, [searchTerm, categories]);

  const displayFilters = selectedCategory
    ? categories.find((c) => c.id === selectedCategory)?.filters || []
    : filteredFilters || [];

  const isFilterDisabled = (filter) =>
    activeFilters.some((f) => f.id === filter.id)
    || barFilterIds.includes(filter.id);

  const handleFilterClick = (filter) => {
    if (isFilterDisabled(filter)) return;
    onSelectFilter(filter);
    onClose();
    setSearchTerm("");
    setSelectedCategory(null);
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={() => {
        onClose();
        setSearchTerm("");
        setSelectedCategory(null);
      }}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      PaperProps={{
        sx: {
          mt: 1,
          borderRadius: "8px",
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
          border: `1px solid ${colors.neutral[200]}`,
          minWidth: 600,
          maxWidth: 800,
          backgroundColor: "#fff",
        },
      }}
    >
      <Box sx={{ p: 2, borderBottom: `1px solid ${colors.neutral[200]}` }}>
        <SafeTextField
          fullWidth
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            if (e.target.value) setSelectedCategory(null);
          }}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: colors.neutral[500] }} />
              </InputAdornment>
            ),
          }}
          sx={{
            ...filterInputSx,
          }}
        />
      </Box>
      <Stack direction="row" sx={{ minHeight: 280, backgroundColor: "#fff" }}>
        <Box sx={{ width: 160, borderRight: `1px solid ${colors.neutral[200]}`, py: 1, backgroundColor: "#fff" }}>
          <List disablePadding>
            {categories.map((category) => (
              <ListItem key={category.id} disablePadding>
                <ListItemButton
                  selected={selectedCategory === category.id}
                  onClick={() => {
                    setSelectedCategory(category.id);
                    setSearchTerm("");
                  }}
                  sx={{
                    py: 1,
                    px: 2,
                    "&.Mui-selected": {
                      backgroundColor: "#f0f4ff",
                      "&:hover": { backgroundColor: "#f0f4ff" },
                    },
                    "&:hover": { backgroundColor: "#f0f4ff" },
                  }}
                >
                  <ListItemText
                    primary={category.label}
                    primaryTypographyProps={{
                      sx: {
                        fontSize: 14,
                        fontWeight: selectedCategory === category.id ? 500 : 400,
                        color: selectedCategory === category.id ? colors.primary[300] : colors.neutral[800],
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
        <Box sx={{ flex: 1, borderRight: `1px solid ${colors.neutral[200]}`, py: 1, overflow: "auto", backgroundColor: "#fff" }}>
          <List disablePadding>
            {displayFilters.length > 0 ? (
              displayFilters.map((filter) => {
                const isDisabled = isFilterDisabled(filter);
                return (
                  <ListItem key={filter.id} disablePadding>
                    <ListItemButton
                      disabled={isDisabled}
                      onClick={() => handleFilterClick(filter)}
                      onMouseEnter={() => setHoveredFilter(filter)}
                      onMouseLeave={() => setHoveredFilter(null)}
                      sx={{
                        py: 1,
                        px: 2,
                        opacity: isDisabled ? 0.5 : 1,
                        "&:hover": { backgroundColor: "#f0f4ff" },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <LayerdIcon icon={Filter} size={16} color="neutral" />
                      </ListItemIcon>
                      <ListItemText
                        primary={filter.label}
                        secondary={filter.category}
                        primaryTypographyProps={{
                          sx:  { fontSize: 14, color: colors.neutral[900] },
                        }}
                        secondaryTypographyProps={{
                          sx:  { fontSize: 14, color: colors.neutral[500] },
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })
            ) : (
              <Box sx={{ p: 2 }}>
                <Typography sx={ { fontSize: 14, color: colors.neutral[500] }}>
                  {searchTerm ? "No filters found" : "Select a category"}
                </Typography>
              </Box>
            )}
          </List>
        </Box>
        <Box sx={{ width: 220, p: 2, backgroundColor: "#fff" }}>
          {hoveredFilter ? (
            <>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <LayerdIcon icon={Filter} size={18} color="primaryLight" />
                <Typography sx={ { fontSize: 14, fontWeight: 500, color: colors.neutral[900] }}>
                  {hoveredFilter.label}
                </Typography>
              </Stack>
              <Typography sx={ { fontSize: 14, color: colors.neutral[600] }}>
                {hoveredFilter.description}
              </Typography>
            </>
          ) : (
            <Typography sx={ { fontSize: 14, color: colors.neutral[500], fontStyle: "italic" }}>
              Hover over a filter to see its description
            </Typography>
          )}
        </Box>
      </Stack>
    </Popover>
  );
};

FilterDropdown.propTypes = {
  anchorEl: PropTypes.object,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  eventType: PropTypes.string.isRequired,
  onSelectFilter: PropTypes.func.isRequired,
  activeFilters: PropTypes.array.isRequired,
  barFilterIds: PropTypes.arrayOf(PropTypes.string),
};

const FilterValuePopover = ({ anchorEl, open, onClose, filter, value, onApply, events, eventType }) => {
  const [inputValue, setInputValue] = useState(value || "");
  const [selectedValue, setSelectedValue] = useState(value || "");
  const staticOptions = isStaticOptionsFilter(filter);

  useEffect(() => {
    if (open) {
      setInputValue(value || "");
      setSelectedValue(value || "");
    }
  }, [open, filter, value]);

  const uniqueValues = useMemo(
    () => collectUniqueFilterValues(events, filter, eventType, 20),
    [events, filter, eventType],
  );

  const filteredValues = useMemo(() => {
    if (staticOptions || !inputValue) return uniqueValues;
    return uniqueValues.filter((v) => 
      String(v).toLowerCase().includes(inputValue.toLowerCase())
    );
  }, [inputValue, uniqueValues, staticOptions]);

  const handleApply = () => {
    onApply(selectedValue || inputValue);
    onClose();
  };

  const handleStaticSelect = (val) => {
    onApply(val);
    onClose();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      PaperProps={{
        sx: {
          mt: 1,
          borderRadius: "8px",
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
          border: `1px solid ${colors.neutral[200]}`,
          width: 280,
          backgroundColor: "#fff",
        },
      }}
    >
      <Box sx={{ p: 2, backgroundColor: "#fff" }}>
        <Typography sx={ { fontSize: 14, fontWeight: 500, mb: 1, color: colors.neutral[800] }}>
          {filter?.label}
        </Typography>
        {!staticOptions && (
          <SafeTextField
            fullWidth
            placeholder="Enter value or select below..."
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setSelectedValue("");
            }}
            size="small"
            sx={{
              mb: 1,
              ...filterInputSx,
            }}
          />
        )}
        {filteredValues.length > 0 && (
          <Box sx={{ maxHeight: 180, overflow: "auto", mb: staticOptions ? 0 : 1, backgroundColor: "#fff" }}>
            <List disablePadding dense>
              {filteredValues.map((val, idx) => (
                <ListItem key={idx} disablePadding>
                  <ListItemButton
                    selected={!staticOptions && selectedValue === val}
                    onClick={() => {
                      if (staticOptions) {
                        handleStaticSelect(val);
                        return;
                      }
                      setSelectedValue(val);
                      setInputValue(val);
                    }}
                    sx={{
                      py: 0.5,
                      px: 1,
                      borderRadius: "8px",
                      "&.Mui-selected": { backgroundColor: "#f0f4ff" },
                      "&:hover": { backgroundColor: "#f0f4ff" },
                    }}
                  >
                    <ListItemText
                      primary={val}
                      primaryTypographyProps={{
                        sx:  { fontSize: 14, color: colors.neutral[800] },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        )}
        {!staticOptions && (
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Chip
            label="Cancel"
            size="small"
            onClick={onClose}
            sx={{
              height: 36,
              fontSize: 14,
              borderRadius: "8px",
              backgroundColor: "#fff",
              border: `1px solid ${colors.neutral[200]}`,
              "&:hover": { borderColor: colors.neutral[400] },
            }}
          />
          <Chip
            label="Apply"
            size="small"
            onClick={handleApply}
            sx={{
              height: 36,
              fontSize: 14,
              borderRadius: "8px",
              backgroundColor: colors.primary[300],
              color: "white",
              "&:hover": { backgroundColor: colors.primary[400] },
            }}
          />
        </Stack>
        )}
      </Box>
    </Popover>
  );
};

FilterValuePopover.propTypes = {
  anchorEl: PropTypes.object,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  filter: PropTypes.object,
  value: PropTypes.string,
  onApply: PropTypes.func.isRequired,
  events: PropTypes.array,
  eventType: PropTypes.string,
};

const EventFilterBar = ({
  eventType,
  activeFilters,
  onFiltersChange,
  events,
  timeRangeComponent,
  onRefresh,
  refreshing,
  search,
  onSearchChange,
  searchPlaceholder = "Search host, URI, IP, UA…",
  searchPending = false,
}) => {
  const [addFilterAnchor, setAddFilterAnchor] = useState(null);
  const [editFilterAnchor, setEditFilterAnchor] = useState(null);
  const [editingFilter, setEditingFilter] = useState(null);
  const [predefinedValues, setPredefinedValues] = useState({});
  const predefinedFilters = useMemo(() => getPredefinedFilters(eventType), [eventType]);

  useEffect(() => {
    const newPredefinedValues = {};
    predefinedFilters.forEach((pf) => {
      const activeFilter = activeFilters.find((af) => af.id === pf.id)
        || (eventType === "ratelimit" && pf.id === "status"
          ? activeFilters.find((af) => af.id === "action")
          : null);
      if (activeFilter && activeFilter.value) {
        newPredefinedValues[pf.id] = activeFilter.value;
      }
    });
    setPredefinedValues(newPredefinedValues);
  }, [activeFilters, eventType, predefinedFilters]);

  const handlePredefinedFilterChange = (filterId, value) => {
    setPredefinedValues((prev) => ({ ...prev, [filterId]: value }));
    const normalizedId = filterId;
    const filtersWithoutLegacyStatus = eventType === "ratelimit" && filterId === "status"
      ? activeFilters.filter((f) => f.id !== "action")
      : activeFilters;
    const existingIndex = filtersWithoutLegacyStatus.findIndex((f) => f.id === normalizedId);
    const filterDef = predefinedFilters.find((f) => f.id === filterId);
    if (existingIndex >= 0) {
      const newFilters = filtersWithoutLegacyStatus.map((f) =>
        f.id === normalizedId ? { ...f, value, label: filterDef?.label || f.label } : f
      );
      onFiltersChange(newFilters);
    } else {
      const newFilters = [
        ...filtersWithoutLegacyStatus,
        { id: normalizedId, label: filterDef?.label || filterId, value },
      ];
      onFiltersChange(newFilters);
    }
  };

  const handlePredefinedFilterClear = (filterId) => {
    setPredefinedValues((prev) => {
      const newValues = { ...prev };
      delete newValues[filterId];
      return newValues;
    });
    const idsToClear = filterId === "status" && eventType === "ratelimit"
      ? ["status", "action"]
      : [filterId];
    const newFilters = activeFilters.filter((f) => !idsToClear.includes(f.id));
    onFiltersChange(newFilters);
  };

  const handleAddFilter = (filter) => {
    const newFilters = [...activeFilters, { ...filter, value: "" }];
    onFiltersChange(newFilters);
  };

  const handleRemoveFilter = (filterId) => {
    if (predefinedFilters.some((f) => f.id === filterId)) {
      handlePredefinedFilterClear(filterId);
    } else {
      const newFilters = activeFilters.filter((f) => f.id !== filterId);
      onFiltersChange(newFilters);
    }
  };

  const handleFilterClick = (event, filter) => {
    setEditFilterAnchor(event.currentTarget);
    setEditingFilter(filter);
  };

  const handleFilterValueApply = (value) => {
    const newFilters = activeFilters.map((f) =>
      f.id === editingFilter.id ? { ...f, value } : f
    );
    onFiltersChange(newFilters);
    setEditingFilter(null);
    if (predefinedFilters.some((f) => f.id === editingFilter.id)) {
      setPredefinedValues((prev) => ({ ...prev, [editingFilter.id]: value }));
    }
  };

  const handleClearAll = () => {
    setPredefinedValues({});
    onFiltersChange([]);
  };

  const customActiveFilters = activeFilters.filter(
    (f) => !predefinedFilters.some((pf) => pf.id === f.id)
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        p: 2,
        backgroundColor: "white",
        borderRadius: "8px",
        border: `1px solid ${colors.neutral[200]}`,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ gap: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" sx={{ gap: 1.5, flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <LayerdIcon icon={Filter} size={18} color="primary" />
            <Typography sx={ { fontSize: 14, fontWeight: 500, color: colors.neutral[800] }}>
              Filters
            </Typography>
          </Stack>

          {typeof search === "string" && typeof onSearchChange === "function" ? (
            <SafeTextField
              sanitize={false}
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              sx={eventSearchInputSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      className="event-search-icon"
                      sx={{
                        fontSize: 16,
                        color: brandBlue,
                        transition: "color 140ms ease",
                      }}
                    />
                  </InputAdornment>
                ),
                endAdornment: (search || searchPending) ? (
                  <InputAdornment position="end">
                    {searchPending ? (
                      <CircularProgress size={14} thickness={5} sx={{ color: brandBlue, mr: 0.5 }} />
                    ) : null}
                    {search ? (
                      <IconButton
                        size="small"
                        aria-label="Clear search"
                        onClick={() => onSearchChange("")}
                        edge="end"
                        sx={{
                          p: 0.4,
                          borderRadius: "6px",
                          color: brandBlue,
                          transition: "background-color 120ms ease, color 120ms ease",
                          "&:hover": {
                            backgroundColor: "rgba(99, 113, 247, 0.12)",
                            color: brandBlue,
                          },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 14, color: "inherit" }} />
                      </IconButton>
                    ) : null}
                  </InputAdornment>
                ) : null,
              }}
            />
          ) : null}

          {predefinedFilters.map((filter) => {
            const filterWithOptions =
              filter.id === "status" && eventType === "bot"
                ? { ...filter, options: ["Blocked", "Challenged", "Detect"] }
                : filter.id === "status" && eventType === "ratelimit"
                  ? { ...filter, options: RATE_LIMIT_STATUS_OPTIONS }
                  : filter.id === "status" && (eventType === "waf" || eventType === "firewall")
                    ? { ...filter, options: ["Blocked", "Challenged", "Passed"] }
                    : filter;
            return (
              <PredefinedFilterDropdown
                key={filter.id}
                filter={filterWithOptions}
                events={events}
                eventType={eventType}
                value={predefinedValues[filter.id] || ""}
                onChange={handlePredefinedFilterChange}
                onClear={handlePredefinedFilterClear}
              />
            );
          })}

          <Chip
            icon={<AddIcon sx={{ fontSize: 16 }} />}
            label="More Filters"
            onClick={(e) => setAddFilterAnchor(e.currentTarget)}
            sx={{
              height: 36,
              borderRadius: "8px",
              backgroundColor: "#fff",
              border: `1px dashed ${colors.neutral[300]}`,
              fontSize: 14,
              color: colors.neutral[800],
              transition: "border-color 140ms ease, color 140ms ease, background-color 140ms ease, box-shadow 140ms ease",
              "& .MuiChip-icon": { color: brandBlue },
              "&:hover": {
                borderColor: brandBlue,
                color: brandBlue,
                backgroundColor: "rgba(99, 113, 247, 0.06)",
                boxShadow: "0 1px 4px rgba(99, 113, 247, 0.12)",
              },
            }}
          />

          {activeFilters.length > 0 && (
            <Chip
              label="Clear All"
              size="small"
              onClick={handleClearAll}
              sx={{
                height: 36,
                fontSize: 14,
                borderRadius: "8px",
                backgroundColor: "#fff",
                border: `1px solid ${colors.neutral[200]}`,
                color: colors.neutral[800],
                "&:hover": {
                  borderColor: colors.red[100],
                  color: colors.red[100],
                },
              }}
            />
          )}
        </Stack>

        <Stack direction="row" alignItems="center" spacing={2} sx={{ flexShrink: 0 }}>
          {onRefresh && (
            <RefreshIconButton
              onClick={onRefresh}
              disabled={refreshing}
              loading={refreshing}
              size="compact"
            />
          )}

          {timeRangeComponent && (
            <Box>
              {timeRangeComponent}
            </Box>
          )}
        </Stack>
      </Stack>

      {customActiveFilters.length > 0 && (
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
          <Typography sx={ { fontSize: 14, color: colors.neutral[600], mr: 1 }}>
            Additional:
          </Typography>
          {customActiveFilters.map((filter) => (
            <FilterChip
              key={filter.id}
              filter={filter}
              value={filter.value}
              onRemove={() => handleRemoveFilter(filter.id)}
              onClick={(e) => handleFilterClick(e, filter)}
            />
          ))}
        </Stack>
      )}

      <FilterDropdown
        anchorEl={addFilterAnchor}
        open={Boolean(addFilterAnchor)}
        onClose={() => setAddFilterAnchor(null)}
        eventType={eventType}
        onSelectFilter={handleAddFilter}
        activeFilters={activeFilters}
        barFilterIds={predefinedFilters.map((f) => f.id)}
      />

      <FilterValuePopover
        anchorEl={editFilterAnchor}
        open={Boolean(editFilterAnchor)}
        onClose={() => {
          setEditFilterAnchor(null);
          setEditingFilter(null);
        }}
        filter={editingFilter}
        value={editingFilter?.value}
        onApply={handleFilterValueApply}
        events={events}
        eventType={eventType}
      />
    </Box>
  );
};

EventFilterBar.propTypes = {
  eventType: PropTypes.oneOf(["waf", "firewall", "bot", "ratelimit"]).isRequired,
  activeFilters: PropTypes.array.isRequired,
  onFiltersChange: PropTypes.func.isRequired,
  events: PropTypes.array,
  timeRangeComponent: PropTypes.node,
  onRefresh: PropTypes.func,
  refreshing: PropTypes.bool,
  search: PropTypes.string,
  onSearchChange: PropTypes.func,
  searchPlaceholder: PropTypes.string,
  searchPending: PropTypes.bool,
};

EventFilterBar.defaultProps = {
  events: [],
  timeRangeComponent: null,
  onRefresh: null,
  refreshing: false,
};

export default EventFilterBar;
